import type { RefObject } from 'react';
import { useRef } from 'react';

import { easeInOutCubic } from '@/shared/lib/animations/easeInOutCubic';

interface UseSmoothScrollReturn {
  smoothScrollTo: (targetScroll: number) => void;
  smoothScrollBy: (distance: number) => void;
}

export const useSmoothScroll = (containerRef: RefObject<HTMLElement>, duration = 300): UseSmoothScrollReturn => {
  const isScrolling = useRef(false);

  const smoothScrollTo = (targetScroll: number) => {
    if (isScrolling.current || !containerRef.current) return;

    isScrolling.current = true;
    const startScroll = containerRef.current.scrollLeft;
    const distance = targetScroll - startScroll;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      if (!containerRef.current) return;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);

      containerRef.current.scrollLeft = startScroll + distance * easeProgress;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        isScrolling.current = false;
      }
    };

    requestAnimationFrame(animate);
  };

  const smoothScrollBy = (distance: number) => {
    if (containerRef.current) {
      smoothScrollTo(containerRef.current.scrollLeft + distance);
    }
  };

  return { smoothScrollTo, smoothScrollBy };
};
